import {useMemo, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useMutation} from "@apollo/client";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";

import {AMENDMENT_DIRECTION, buildShiftOptionLabel} from "../../helpers/ShiftHelper";
import {MINUTES_PER_HOUR} from "../../helpers/DateTimeRangeHelper";
import {AMENDMENT_ATTACHMENT_SCOPES, AMENDMENT_DURATION_UNITS} from "../../domain/AmendmentConstants";
import {CREATE_AMENDMENT} from "../../queries/GraphQLQueries";
import CustomAlert from "../CustomAlert";
import AmendmentDetailsForm from "./AmendmentDetailsForm";

const styles = {
    dialog: {
        closeButton: {
            position: 'absolute',
            right: 20,
            top: 12,
        },
        content: {
            padding: "1rem",
        },
        title: {
            padding: "1rem",
        },
        actions: {
            padding: "1rem",
            paddingTop: 0,
        },
    },
};

export default function ShiftAdminAddAmendmentDialog({open, onClose, performer, timezone, onAmendmentCreated}) {
    const {t} = useTranslation();
    const [createAmendment, {loading: isCreatingAmendment}] = useMutation(CREATE_AMENDMENT);

    const [amendmentScope, setAmendmentScope] = useState(AMENDMENT_ATTACHMENT_SCOPES.STANDALONE);
    const [amendmentDirection, setAmendmentDirection] = useState(AMENDMENT_DIRECTION.CREDIT);
    const [amendmentType, setAmendmentType] = useState(null);
    const [selectedShiftId, setSelectedShiftId] = useState(null);
    const [durationValue, setDurationValue] = useState("");
    const [durationUnit, setDurationUnit] = useState(AMENDMENT_DURATION_UNITS.MINUTES);
    const [incidentTime, setIncidentTime] = useState(null);
    const [notes, setNotes] = useState("");
    const [alertMessage, setAlertMessage] = useState(null);

    const resetDialog = () => {
        setAmendmentScope(AMENDMENT_ATTACHMENT_SCOPES.STANDALONE);
        setAmendmentDirection(AMENDMENT_DIRECTION.CREDIT);
        setAmendmentType(null);
        setSelectedShiftId(null);
        setDurationValue("");
        setDurationUnit(AMENDMENT_DURATION_UNITS.MINUTES);
        setIncidentTime(null);
        setNotes("");
        setAlertMessage(null);
    };

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const shiftOptions = useMemo(() => {
        const shifts = performer?.shifts || [];
        return shifts
            .map((shift) => ({
                value: shift.id,
                label: buildShiftOptionLabel(shift, timezone),
            }))
            .filter((shiftOption) => Boolean(shiftOption.label));
    }, [performer, timezone]);

    const selectedShift = useMemo(() => {
        const shifts = performer?.shifts || [];
        return shifts.find((shift) => shift.id === selectedShiftId);
    }, [performer, selectedShiftId]);

    const handleScopeChange = (_, value) => {
        if (!value) {
            return;
        }

        setAmendmentScope(value);
        if (value === AMENDMENT_ATTACHMENT_SCOPES.STANDALONE) {
            setSelectedShiftId(null);
        }
    };

    const handleDirectionChange = (_, value) => {
        if (value) {
            setAmendmentDirection(value);
        }
    };

    const handleShiftChange = (event) => {
        setSelectedShiftId(event.target.value);
    };

    const handleAmendmentTypeChange = (event) => {
        setAmendmentType(event.target.value);
    };

    const handleIncidentTimeChange = (newValue) => {
        setIncidentTime(newValue);
    };

    const handleDurationValueChange = (event) => {
        const nextDurationValue = event.target.value;
        setDurationValue(nextDurationValue === "" ? "" : Math.abs(Number(nextDurationValue)));
    };

    const handleDurationUnitChange = (event) => {
        setDurationUnit(event.target.value);
    };

    const handleNotesChange = (event) => {
        setNotes(event.target.value);
    };

    const handleClose = () => {
        resetDialog();
        onClose();
    };

    const isStandaloneAmendment = amendmentScope === AMENDMENT_ATTACHMENT_SCOPES.STANDALONE;
    const isAttachedAmendment = !isStandaloneAmendment;
    const isShiftSelectionMissing = isAttachedAmendment && selectedShiftId === null;
    const shiftStartTime = selectedShift?.performingFrom ? moment(selectedShift.performingFrom) : null;
    const amendmentStartTime = isStandaloneAmendment ? incidentTime : shiftStartTime;
    const durationInMinutes = durationUnit === AMENDMENT_DURATION_UNITS.HOURS
        ? Math.round(durationValue * MINUTES_PER_HOUR)
        : Math.round(durationValue);
    const isDurationInvalid = !Number.isFinite(durationValue) || durationValue <= 0;

    const values = {
        amendmentScope,
        amendmentDirection,
        amendmentType,
        selectedShiftId,
        durationValue,
        durationUnit,
        incidentTime: amendmentStartTime,
        notes,
    };

    const actions = {
        handleScopeChange,
        handleDirectionChange,
        handleShiftChange,
        handleAmendmentTypeChange,
        handleIncidentTimeChange,
        handleDurationValueChange,
        handleDurationUnitChange,
        handleNotesChange,
    };

    const isMissingRequiredFields = !performer?.id || !amendmentType || !amendmentStartTime || !notes.trim();
    const isSubmitDisabled = isCreatingAmendment || isMissingRequiredFields || isShiftSelectionMissing || isDurationInvalid;

    const handleCreateAmendment = async () => {
        setAlertMessage(null);

        if (isSubmitDisabled) {
            return;
        }

        try {
            setAlertMessage({children: t("shiftAdminAddAmendmentDialog.creatingAmendment"), severity: "success"});

            await createAmendment({
                variables: {
                    performerId: performer.id,
                    shiftId: isAttachedAmendment ? selectedShiftId : null,
                    amendmentType,
                    startTime: amendmentStartTime.toISOString(),
                    durationInMinutes,
                    direction: amendmentDirection,
                    notes: notes.trim(),
                },
            });

            await onAmendmentCreated?.();
            handleClose();
        } catch (error) {
            setAlertMessage({children: error?.message, severity: "error"});
            console.error(error);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={styles.dialog.title}>
                        <Typography variant="h5" fontWeight={700}>
                            {t("shiftAdminAddAmendmentDialog.addAmendmentLabel")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {performer?.fullName}
                        </Typography>
                </DialogTitle>
                <IconButton aria-label="close" onClick={handleClose} sx={styles.dialog.closeButton}>
                    <CloseIcon/>
                </IconButton>
                <Divider/>
                <DialogContent sx={styles.dialog.content}>
                    <AmendmentDetailsForm
                        values={values}
                        shiftOptions={shiftOptions}
                        actions={actions}/>
                </DialogContent>

                <DialogActions sx={styles.dialog.actions}>
                    <Button onClick={handleClose} variant="contained">
                        {t("common.cancelLabel")}
                    </Button>

                    <Button variant="contained" onClick={handleCreateAmendment} disabled={isSubmitDisabled}>
                        {t("shiftAdminAddAmendmentDialog.addAmendmentLabel")}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
        </>
    );
}